import React, { useEffect, useState } from 'react';
import { useParams, NavLink, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTenants } from '../../services/adminApi';
import Identity from './Identity';
import Subdomain from './Subdomain';
import ProviderConfig from './ProviderConfig';
import Scoring from './Scoring';
import Questions from './Questions';
import Members from './Members';

// Per-tenant sub-sections. These pages are no longer top-level nav items — they are configured
// in the context of a selected tenant, so each renders embedded here with the tenant fixed via
// the `tenantCode` prop (their own tenant picker is hidden in that mode).
const TABS = [
    { key: 'identity',  label: 'Identity',       icon: 'key' },
    { key: 'domain',    label: 'Domain',         icon: 'sitemap' },
    { key: 'config',    label: 'Configuration',  icon: 'sliders-h' },
    { key: 'scoring',   label: 'Scoring',        icon: 'chart-pie' },
    { key: 'questions', label: 'Questions',      icon: 'clipboard-list' },
    { key: 'members',   label: 'Members',        icon: 'users' },
];

function TenantDetail() {
    const { code, tab } = useParams();
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);
        fetchTenants()
            .then((d) => {
                if (cancelled) return;
                const t = (d.tenants || []).find((x) => x.code === code);
                if (t) setTenant(t);
                else setNotFound(true);
            })
            .catch(() => { if (!cancelled) setNotFound(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [code]);

    // Bare /tenants/:code → default to the first sub-section.
    if (!tab) return <Navigate to={`/tenants/${code}/identity`} replace />;

    const renderTab = () => {
        switch (tab) {
            case 'identity':  return <Identity tenantCode={code} />;
            case 'domain':    return <Subdomain tenantCode={code} />;
            case 'config':    return <ProviderConfig tenantCode={code} />;
            case 'scoring':   return <Scoring tenantCode={code} />;
            case 'questions': return <Questions tenantCode={code} />;
            case 'members':   return <Members tenantCode={code} />;
            default:          return <Navigate to={`/tenants/${code}/identity`} replace />;
        }
    };

    return (
        <>
            <div style={{ padding: '20px 32px 0' }}>
                <NavLink to="/tenants" className="text-decoration-none text-muted" style={{ fontSize: 13 }}>
                    <FontAwesomeIcon icon="chevron-left" className="me-1" /> Tenants
                </NavLink>
                <h1 className="page-title mb-3 mt-1">
                    {loading ? 'Loading…' : (notFound ? code : tenant?.name)}
                    {tenant && (
                        <span className="text-muted ms-2" style={{ fontSize: 14, fontWeight: 400, fontFamily: 'monospace' }}>
                            ({tenant.code})
                        </span>
                    )}
                </h1>
                <ul className="nav nav-tabs">
                    {TABS.map((t) => (
                        <li className="nav-item" key={t.key}>
                            <NavLink
                                to={`/tenants/${code}/${t.key}`}
                                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                            >
                                <FontAwesomeIcon icon={t.icon} className="me-2" />{t.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>

            {notFound
                ? <div className="page-content text-muted">Tenant &quot;{code}&quot; not found.</div>
                : renderTab()}
        </>
    );
}

export default TenantDetail;
